import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { StellarDatePicker } from './StellarFormControls';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { CalendarDays, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const YEARS = [2026, 2027, 2028, 2029, 2030];

/**
 * Workspace Holidays — Owner/Admin manager for pre-loaded and custom holidays.
 *
 * Two tabs (India / Canada) × year selector.
 * Pre-loaded entries have a "default" badge; custom entries show "added".
 * Remove button works for both.
 * "Add Holiday" opens a small form to inject custom entries.
 */
export default function WorkspaceHolidaysCard({ workspaceId, role }) {
  const canEdit = ['owner', 'admin', 'super_admin'].includes((role || '').toLowerCase());
  const [region, setRegion] = useState('india');
  const [year, setYear] = useState(2026);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', india: false, canada: false });

  const load = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/api/workspaces/${workspaceId}/holidays?year=${year}&region=${region}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setRows(body.holidays || []);
    } catch (e) {
      toast.error(`Failed to load holidays: ${e.message || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId, region, year]);

  const seedNow = async () => {
    if (!canEdit) return;
    setSeeding(true);
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/holidays/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      toast.success(`Seeded ${body.inserted} pre-loaded holidays`);
      load();
    } catch (e) {
      toast.error(`Seed failed: ${e.message || 'unknown'}`);
    } finally {
      setSeeding(false);
    }
  };

  const remove = async (id) => {
    if (!canEdit) return;
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/holidays/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setRows((prev) => prev.filter((x) => x.id !== id));
      toast.success('Holiday removed');
    } catch (e) {
      toast.error(`Remove failed: ${e.message || 'unknown'}`);
    }
  };

  const submitCustom = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.date) { toast.error('Name and date are required'); return; }
    const applies = [form.india && 'india', form.canada && 'canada'].filter(Boolean);
    if (applies.length === 0) { toast.error('Pick at least one region'); return; }
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/holidays`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify({ name: form.name.trim(), date: form.date, applies_to: applies }),
      });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${r.status}`); }
      setAddOpen(false);
      setForm({ name: '', date: '', india: false, canada: false });
      toast.success('Holiday added');
      load();
    } catch (e) {
      toast.error(`Add failed: ${e.message || 'unknown'}`);
    }
  };

  const empty = rows.length === 0 && !loading;

  const sorted = useMemo(() => [...rows].sort((a, b) => a.date.localeCompare(b.date)), [rows]);

  return (
    <Card data-testid="workspace-holidays-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-gold" /> Workspace Holidays
        </CardTitle>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={seedNow} disabled={seeding}
                    className="h-7 text-[11px]" data-testid="holidays-seed-btn">
              {seeding ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Seed defaults'}
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}
                    className="h-7 text-[11px] bg-gold hover:bg-gold/90 text-black"
                    data-testid="holidays-add-btn">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Tabs value={region} onValueChange={setRegion} className="flex-1">
            <TabsList className="grid grid-cols-2 w-[220px]" data-testid="holidays-region-tabs">
              <TabsTrigger value="india">India</TabsTrigger>
              <TabsTrigger value="canada">Canada</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-1">
            {YEARS.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={
                  'px-2 py-0.5 rounded text-[11px] border transition-colors ' +
                  (year === y ? 'bg-gold text-black border-gold' : 'border-border/30 text-muted-foreground/80 hover:text-gold')
                }
                data-testid={`holidays-year-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="py-4 text-center text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin inline-block mr-1" /> Loading…
          </div>
        )}

        {empty && (
          <div className="text-center py-5">
            <p className="text-[12px] text-muted-foreground/70 mb-2">
              No holidays set for {region === 'india' ? 'India' : 'Canada'} in {year}.
            </p>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={seedNow} disabled={seeding}>
                Seed pre-loaded defaults (2026–2030)
              </Button>
            )}
          </div>
        )}

        {!empty && !loading && (
          <div className="divide-y divide-border/20 rounded-md border border-border/30">
            {sorted.map((h) => (
              <div key={h.id} className="flex items-center gap-3 px-2.5 py-1.5 text-[12px]"
                   data-testid={`holiday-row-${h.id}`}>
                <span className="font-mono text-muted-foreground/80 w-[92px] shrink-0">{h.date}</span>
                <span className="flex-1 text-foreground/90">{h.name}</span>

                {/* Region badges — show IN / CA tags so the Owner can
                    audit at a glance which team each holiday applies to.
                    Dual-region entries render both badges. */}
                <div className="flex gap-1 shrink-0">
                  {(h.applies_to || []).includes('india') && (
                    <Badge className="text-[9px] px-1.5 py-0 bg-orange-500/15 text-orange-300 border-orange-500/30"
                           data-testid={`holiday-region-india-${h.id}`}>
                      IN
                    </Badge>
                  )}
                  {(h.applies_to || []).includes('canada') && (
                    <Badge className="text-[9px] px-1.5 py-0 bg-red-500/15 text-red-300 border-red-500/30"
                           data-testid={`holiday-region-canada-${h.id}`}>
                      CA
                    </Badge>
                  )}
                </div>

                <Badge className={
                  'text-[9px] px-1.5 py-0 shrink-0 ' +
                  (h.custom
                    ? 'bg-gold/20 text-gold border-gold/30'
                    : 'bg-zinc-700/30 text-zinc-300 border-zinc-600/40')
                }>
                  {h.custom ? 'added' : 'default'}
                </Badge>
                {canEdit && (
                  <button
                    onClick={() => remove(h.id)}
                    className="text-muted-foreground/40 hover:text-destructive shrink-0"
                    data-testid={`holiday-remove-${h.id}`}
                    title="Remove"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold">Add custom holiday</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCustom} className="space-y-3">
            <div>
              <label className="text-[11px] font-medium block mb-1">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g., Team retreat" data-testid="holiday-add-name" />
            </div>
            <div>
              <label className="text-[11px] font-medium block mb-1">Date</label>
              <StellarDatePicker
                value={form.date}
                onChange={(d) => setForm({ ...form, date: d })}
                placeholder="Select holiday date"
                testId="holiday-add-date"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium block mb-1">Applies to</label>
              <div className="flex gap-3 text-[12px]">
                <label className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={form.india}
                         onChange={(e) => setForm({ ...form, india: e.target.checked })}
                         data-testid="holiday-add-india" />
                  India
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={form.canada}
                         onChange={(e) => setForm({ ...form, canada: e.target.checked })}
                         data-testid="holiday-add-canada" />
                  Canada
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-gold hover:bg-gold/90 text-black"
                      data-testid="holiday-add-submit">Add holiday</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
